if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('[data-product-form-submit-text]') || this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.boundOnAddonOrQuantityChange = this.updateSubmitButtonPrice.bind(this);
        document.addEventListener('change', this.boundOnAddonOrQuantityChange);
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.boundOnAddonOrQuantityChange);
      }

      formatMoney(cents, format) {
        if (!format || cents == null) return '';
        const amount = (cents / 100).toFixed(2);
        const amountComma = amount.replace('.', ',');
        const amountInt = Math.round(cents / 100).toString();
        const amountIntComma = amountInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format
          .replace(/\{\{\s*amount\s*\}\}/g, amount)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountInt)
          .replace(/\{\{\s*amount_with_comma_separator_no_decimals\s*\}\}/g, amountIntComma);
      }

      updateSubmitButtonPrice(evt) {
        if (evt && evt.target !== document) {
          const isAddon = evt.target.matches && evt.target.matches('input[data-product-addon-variant-id]');
          const isQuantity = evt.target.name === 'quantity' && this.form && this.form.contains(evt.target);
          if (!isAddon && !isQuantity) return;
        }

        const span = this.submitButton?.querySelector('[data-product-form-submit-text]') || this.submitButtonText;
        if (!span || !span.dataset.basePrice) return;
        const showPrice = span.dataset.showPrice;
        if (showPrice === 'false' || showPrice === '') return;

        const label = span.dataset.buttonLabel || '';
        const basePrice = parseInt(span.dataset.basePrice, 10);
        const moneyFormat = span.dataset.moneyFormat || '{{amount}} kr';
        if (isNaN(basePrice)) return;

        const quantityInput = this.form.querySelector('[name="quantity"]');
        const quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value, 10) || 1) : 1;
        let totalCents = basePrice * quantity;

        const addonCheckboxes = document.querySelectorAll('input[data-product-addon-variant-id]:checked');
        addonCheckboxes.forEach((cb) => {
          const price = parseInt(cb.getAttribute('data-product-addon-price'), 10);
          if (!isNaN(price)) totalCents += price;
        });

        const formatted = this.formatMoney(totalCents, moneyFormat);
        span.textContent = label ? `${label} • ${formatted}` : formatted;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const addonCheckboxes = document.querySelectorAll('input[data-product-addon-variant-id]:checked');
        const hasAddons = addonCheckboxes.length > 0;

        if (hasAddons) {
          this.submitWithAddons(evt, addonCheckboxes);
          return;
        }

        this.submitSingle(evt);
      }

      submitSingle(evt) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => this.handleAddToCartResponse(response, formData.get('id'), evt))
          .catch((e) => {
            console.error(e);
          })
          .finally(() => this.resetSubmitButton(evt));
      }

      submitWithAddons(evt, addonCheckboxes) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        if (this.cart) this.cart.setActiveElement(document.activeElement);

        const mainId = this.variantIdInput.value;
        const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);
        const addonIds = Array.from(addonCheckboxes).map((cb) => cb.getAttribute('data-product-addon-variant-id'));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const addOne = (id, qty, includeSections) => {
          const body = new URLSearchParams({ id, quantity: String(qty) });
          if (includeSections && this.cart) {
            body.append('sections', this.cart.getSectionsToRender().map((s) => s.id).join(','));
            body.append('sections_url', window.location.pathname);
          }
          config.body = body;
          return fetch(`${routes.cart_add_url}`, config).then((res) => res.json());
        };

        let promise = addOne(mainId, quantity, addonIds.length === 0);
        addonIds.forEach((addonId, i) => {
          const isLast = i === addonIds.length - 1;
          promise = promise.then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message);
              this.error = true;
              return Promise.reject(response);
            }
            return addOne(addonId, 1, isLast);
          });
        });

        promise
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message);
              this.error = true;
              return;
            }
            this.handleAddToCartResponse(response, mainId, evt);
          })
          .catch(() => {})
          .finally(() => this.resetSubmitButton(evt));
      }

      handleAddToCartResponse(response, productVariantId, evt) {
        if (this.error) return;
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId,
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart.renderContents(response);
          });
        }
        CartPerformance.measureFromEvent("add:user-action", evt);
      }

      resetSubmitButton(evt) {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
        if (evt && !this.error) CartPerformance.measureFromEvent("add:user-action", evt);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          /* When block uses custom button label (e.g. Kanel), keep server-rendered text; else use theme default */
          if (!this.submitButtonText.dataset.buttonLabel) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
